import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, FolderOpen, Play, Plug, RefreshCw, X } from 'lucide-react';
import type {
  DialectDescriptor,
  DialectId,
  ImportJobStatus,
  ImportPhase,
} from '@bits2schema/shared';
import { api } from '../api/client';
import { useT } from '../i18n';
import type { MessageKey } from '../i18n/types';
import {
  describeError,
  emptyConnectionDraft,
  isConnectionDraftComplete,
  toConnectionInput,
  type ConnectionDraft,
} from '../utils/format';
import { ConnectionForm } from './ConnectionForm';
import { ImportWarnings } from './ImportWarnings';

type Step = 'project' | 'connection' | 'schemas' | 'run';

const PHASE_KEYS: Record<ImportPhase, MessageKey> = {
  connecting: 'import.phase.connecting',
  readingTables: 'import.phase.readingTables',
  readingColumns: 'import.phase.readingColumns',
  readingConstraints: 'import.phase.readingConstraints',
  readingIndexes: 'import.phase.readingIndexes',
  buildingModel: 'import.phase.buildingModel',
  savingProject: 'import.phase.savingProject',
  done: 'import.phase.done',
};

const POLL_INTERVAL_MS = 500;

interface ImportWizardProps {
  dialects: DialectDescriptor[];
  onCancel: () => void;
  onImported: (projectId: string) => void;
}

/** E1-US4 — průvodce založením projektu importem z databáze. */
export function ImportWizard({ dialects, onCancel, onImported }: ImportWizardProps) {
  const t = useT();
  const [step, setStep] = useState<Step>('project');
  const [name, setName] = useState('');
  const [dialect, setDialect] = useState<DialectId>(dialects[0]?.dialectId ?? 'postgresql');
  const [draft, setDraft] = useState<ConnectionDraft>(emptyConnectionDraft);
  const [testState, setTestState] = useState<'idle' | 'testing' | 'ok' | 'failed'>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [schemas, setSchemas] = useState<string[]>([]);
  const [selectedSchemas, setSelectedSchemas] = useState<string[]>([]);
  const [job, setJob] = useState<ImportJobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const descriptor = dialects.find((item) => item.dialectId === dialect);

  const runTest = useCallback(async () => {
    setTestState('testing');
    setError(null);
    try {
      const result = await api.testConnection(dialect, toConnectionInput(dialect, draft));
      if (result.ok) {
        setTestState('ok');
        setTestMessage(
          result.serverVersion
            ? t('import.testOk', { version: result.serverVersion })
            : t('import.testOkNoVersion'),
        );
      } else {
        setTestState('failed');
        setTestMessage(t('import.testFailed', { message: result.message ?? '' }));
      }
    } catch (caught) {
      setTestState('failed');
      setTestMessage(describeError(caught, t));
    }
  }, [dialect, draft, t]);

  const loadSchemas = useCallback(async () => {
    setError(null);
    try {
      const result = await api.listSchemas(dialect, toConnectionInput(dialect, draft));
      setSchemas(result.schemas);
      setSelectedSchemas(result.schemas);
    } catch (caught) {
      setError(describeError(caught, t));
    }
  }, [dialect, draft, t]);

  const goToSchemas = useCallback(async () => {
    setStep('schemas');
    if (descriptor?.supportsSchemaSelection) await loadSchemas();
  }, [descriptor, loadSchemas]);

  const startImport = useCallback(async () => {
    setError(null);
    setStep('run');
    try {
      const { jobId } = await api.startImport({
        name,
        dialect,
        connection: toConnectionInput(dialect, draft),
        schemas: selectedSchemas,
      });
      setJob({
        jobId,
        state: 'running',
        phase: 'connecting',
        tablesDone: 0,
        tablesTotal: null,
        startedAt: new Date().toISOString(),
        finishedAt: null,
      });
    } catch (caught) {
      setError(describeError(caught, t));
      setStep('schemas');
    }
  }, [dialect, draft, name, selectedSchemas, t]);

  // Průběh importu se dotazuje periodicky, dokud úloha běží (E1-US4).
  useEffect(() => {
    if (!job || job.state !== 'running') return undefined;
    const timer = window.setInterval(async () => {
      try {
        setJob(await api.getImportJob(job.jobId));
      } catch (caught) {
        setError(describeError(caught, t));
        window.clearInterval(timer);
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [job, t]);

  const canLeaveProjectStep = name.trim().length > 0;
  const canTest = isConnectionDraftComplete(dialect, draft);

  return (
    <section className="card">
      <h1>{t('import.title')}</h1>
      <ol className="steps">
        <li aria-current={step === 'project' ? 'step' : undefined}>{t('import.step.project')}</li>
        <li aria-current={step === 'connection' ? 'step' : undefined}>
          {t('import.step.connection')}
        </li>
        <li aria-current={step === 'schemas' ? 'step' : undefined}>{t('import.step.schemas')}</li>
        <li aria-current={step === 'run' ? 'step' : undefined}>{t('import.step.run')}</li>
      </ol>

      {error && <div className="notice notice--error">{error}</div>}

      {step === 'project' && (
        <div className="stack">
          <div className="row">
            <div className="field">
              <label htmlFor="project-name">{t('import.nameLabel')}</label>
              <input
                id="project-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="project-dialect">{t('import.dialectLabel')}</label>
              <select
                id="project-dialect"
                value={dialect}
                onChange={(event) => {
                  setDialect(event.target.value as DialectId);
                  setTestState('idle');
                }}
              >
                {dialects.map((item) => (
                  <option key={item.dialectId} value={item.dialectId}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal__actions">
            <button type="button" onClick={onCancel}>
              <X size={16} />
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="primary"
              disabled={!canLeaveProjectStep}
              onClick={() => setStep('connection')}
            >
              {t('common.next')}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 'connection' && (
        <div className="stack">
          <ConnectionForm dialect={dialect} draft={draft} onChange={setDraft} />
          <p className="hint">{t('import.readOnlyHint')}</p>

          {testState === 'testing' && <div className="notice">{t('import.testing')}</div>}
          {testState === 'ok' && <div className="notice notice--success">{testMessage}</div>}
          {testState === 'failed' && <div className="notice notice--error">{testMessage}</div>}

          <div className="modal__actions">
            <button type="button" onClick={() => setStep('project')}>
              <ArrowLeft size={16} />
              {t('common.back')}
            </button>
            <button type="button" disabled={!canTest} onClick={() => void runTest()}>
              <Plug size={16} />
              {t('import.testConnection')}
            </button>
            <button
              type="button"
              className="primary"
              disabled={testState !== 'ok'}
              onClick={() => void goToSchemas()}
            >
              {t('common.next')}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 'schemas' && (
        <div className="stack">
          {descriptor?.supportsSchemaSelection ? (
            <>
              <h2>{t('import.schemasTitle')}</h2>
              <p className="hint">{t('import.schemasHint')}</p>
              {schemas.length === 0 ? (
                <p className="hint">{t('import.schemasEmpty')}</p>
              ) : (
                <div className="checklist">
                  {schemas.map((schema) => (
                    <label key={schema}>
                      <input
                        type="checkbox"
                        checked={selectedSchemas.includes(schema)}
                        onChange={(event) =>
                          setSelectedSchemas((current) =>
                            event.target.checked
                              ? [...current, schema]
                              : current.filter((item) => item !== schema),
                          )
                        }
                      />
                      {schema}
                    </label>
                  ))}
                </div>
              )}
              <button type="button" className="link" onClick={() => void loadSchemas()}>
                <RefreshCw size={14} />
                {t('import.schemasLoad')}
              </button>
            </>
          ) : (
            <p className="hint">{t('import.schemasNotSupported')}</p>
          )}

          <div className="modal__actions">
            <button type="button" onClick={() => setStep('connection')}>
              <ArrowLeft size={16} />
              {t('common.back')}
            </button>
            <button
              type="button"
              className="primary"
              disabled={descriptor?.supportsSchemaSelection && selectedSchemas.length === 0}
              onClick={() => void startImport()}
            >
              <Play size={16} />
              {t('import.start')}
            </button>
          </div>
        </div>
      )}

      {step === 'run' && job && <ImportProgress job={job} onOpen={onImported} onCancel={onCancel} />}
    </section>
  );
}

function ImportProgress({
  job,
  onOpen,
  onCancel,
}: {
  job: ImportJobStatus;
  onOpen: (projectId: string) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const percent =
    job.tablesTotal && job.tablesTotal > 0
      ? Math.min(100, Math.round((job.tablesDone / job.tablesTotal) * 100))
      : null;

  return (
    <div className="stack">
      <h2>{t(PHASE_KEYS[job.phase])}</h2>

      {job.state === 'running' && (
        <>
          <div className="progress">
            <div className="progress__bar" style={{ width: `${percent ?? 15}%` }} />
          </div>
          <p className="hint">
            {job.tablesTotal === null
              ? t('import.progressUnknownTotal', { done: job.tablesDone })
              : t('import.progress', { done: job.tablesDone, total: job.tablesTotal })}
          </p>
          <div className="notice">{t('import.running')}</div>
        </>
      )}

      {job.state === 'failed' && (
        <div className="notice notice--error">
          {t('import.failed', { message: job.error?.message ?? '' })} {t('import.failedNoModel')}
        </div>
      )}

      {job.state === 'succeeded' && (
        <>
          <div className="notice notice--success">{t('import.succeeded')}</div>
          <h3>{t('warnings.title')}</h3>
          <ImportWarnings warnings={job.warnings ?? []} />
        </>
      )}

      <div className="modal__actions">
        <button type="button" onClick={onCancel}>
          <X size={16} />
          {job.state === 'running' ? t('common.cancel') : t('common.close')}
        </button>
        {job.state === 'succeeded' && job.projectId && (
          <button type="button" className="primary" onClick={() => onOpen(job.projectId as string)}>
            <FolderOpen size={16} />
            {t('import.openProject')}
          </button>
        )}
      </div>
    </div>
  );
}
